"use client";

import { useCallback, useMemo, useState } from "react";
import { HOME_VIEW, type MessengerTab, type MessengerView } from "./messenger-types";

export interface MessengerNav {
  readonly view: MessengerView;
  /** Unique per opened view: a new chat in place of a new chat still remounts. */
  readonly viewKey: string;
  readonly canGoBack: boolean;
  readonly push: (view: MessengerView) => void;
  /** Swap the current view, e.g. the new chat once it has an id. */
  readonly replace: (view: MessengerView) => void;
  readonly back: () => void;
  readonly selectTab: (tab: MessengerTab) => void;
  readonly reset: (view: MessengerView) => void;
}

interface NavEntry {
  readonly view: MessengerView;
  readonly key: string;
}

let lastEntryId = 0;

function entryOf(view: MessengerView): NavEntry {
  lastEntryId += 1;
  return { view, key: String(lastEntryId) };
}

const HOME_ENTRY: NavEntry = { view: HOME_VIEW, key: "home" };

/** View stack of one messenger window; tabs start a fresh stack. */
export function useMessengerNav(initial: readonly MessengerView[] = [HOME_VIEW]): MessengerNav {
  const [stack, setStack] = useState<readonly NavEntry[]>(() => initial.map(entryOf));

  const push = useCallback((view: MessengerView): void => {
    setStack((prev) => [...prev, entryOf(view)]);
  }, []);
  const replace = useCallback((view: MessengerView): void => {
    setStack((prev) => [...prev.slice(0, -1), entryOf(view)]);
  }, []);
  const back = useCallback((): void => {
    setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);
  const selectTab = useCallback((tab: MessengerTab): void => {
    setStack([entryOf({ kind: tab })]);
  }, []);
  const reset = useCallback((view: MessengerView): void => {
    // Deep links keep a way back to the start view.
    setStack(view.kind === "home" ? [entryOf(view)] : [entryOf(HOME_VIEW), entryOf(view)]);
  }, []);

  const current = stack[stack.length - 1] ?? HOME_ENTRY;
  return useMemo(
    (): MessengerNav => ({
      view: current.view,
      viewKey: current.key,
      canGoBack: stack.length > 1,
      push,
      replace,
      back,
      selectTab,
      reset,
    }),
    [back, current, push, replace, reset, selectTab, stack.length],
  );
}
